/* eslint-disable react/no-multi-comp */
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import clsx from 'clsx';

import { TranslationService } from '@/utils/services/TranslationService';

// Hooks
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useSocialProfilesAuthStatus from '@/utils/hooks/swr/useSocialProfilesAuthStatus';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useUserRole from '@/utils/hooks/user/useUserRole';

import { classNames } from '@/utils';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

import { SOCIAL_PROFILES } from '@/lib/routes/privateRoutes';
// Lib
import { SOCIAL_PROFILES_ACTION_PARAM } from '@/lib/socialprofiles/queryParams';

// Components
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
import Loader from '@/components/base/Loader';
// Layout
import { WrapperSwitchLayout } from '@/components/layouts/wrappers/WrapperSwitchLayout';
import AddSocialProfilesAction from '@/components/pages/socialprofiles/AddSocialProfilesAction';
import { ManageProfilesWrapper } from '@/components/pages/socialprofiles/ManageProfilesWrapper';
import RightPanel from '@/components/pages/socialprofiles/RightPanel';
import SocialProfilesList from '@/components/pages/socialprofiles/SocialProfilesList';

function SocialProfileContent({ isOnTrial, toggleSPFunnelModal, plan }) {
    const { isAdmin, isManager } = useUserRole();

    return (
        <>
            <SocialProfilesList isOnTrial={isOnTrial} addSocialProfile={toggleSPFunnelModal} />
            {isAdmin || isManager ? (
                <div
                    className={clsx(
                        'rounded-md border border-icoGray-300 bg-white px-8 py-8 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack xl:w-1/3'
                    )}>
                    <RightPanel isAdmin={isAdmin} isManager={isManager} plan={plan} />
                </div>
            ) : null}
        </>
    );
}

const PageContent = () => {
    const router = useRouter();
    const { deleteQuery } = useRouterQuery();
    const {
        funnel_add_social_profiles,
        x_sp_id,
        tiktok_business_sp_id,
        youtube_sp_id,
        pinterest_sp_id,
        threads_sp_id
    } = router.query;
    const queryAction = router.query[SOCIAL_PROFILES_ACTION_PARAM.NAME];
    const { t: tErrors } = useTranslation('errors');
    const [stateFunnelSocialProfiles, setStateFunnelSocialProfiles] = useState({
        isOpen: false,
        network: null,
        // For twitter & tiktok
        idSocialProfileAdded: null
    });

    // Open Funnel to add new social profiles
    useEffect(() => {
        if (!stateFunnelSocialProfiles.isOpen && funnel_add_social_profiles) {
            const getSocialProfileAddedId = () => {
                if (x_sp_id) {
                    return parseInt(x_sp_id);
                } else if (tiktok_business_sp_id) {
                    return parseInt(tiktok_business_sp_id);
                } else if (youtube_sp_id) {
                    return parseInt(youtube_sp_id);
                } else if (pinterest_sp_id) {
                    return parseInt(pinterest_sp_id);
                } else if (threads_sp_id) {
                    return parseInt(threads_sp_id);
                } else {
                    return null;
                }
            };
            setStateFunnelSocialProfiles({
                isOpen: true,
                network: funnel_add_social_profiles,
                idSocialProfileAdded: getSocialProfileAddedId()
            });
            const newUrl = deleteQuery([
                'funnel_add_social_profiles',
                'x_sp_id',
                'tiktok_business_sp_id',
                'youtube_sp_id',
                'pinterest_sp_id',
                'threads_sp_id'
            ]);
            router.replace(newUrl);
        }
    }, [funnel_add_social_profiles]);

    useEffect(() => {
        if (queryAction === SOCIAL_PROFILES_ACTION_PARAM.VALUES.ADD_SOCIAL_PROFILES) {
            setStateFunnelSocialProfiles((prevState) => ({
                ...prevState,
                isOpen: true
            }));
        }
        const newUrl = deleteQuery(SOCIAL_PROFILES_ACTION_PARAM.NAME);
        router.replace(newUrl);
    }, [queryAction]);

    const { subscription: plan, isLoading, isOnTrial, subscriptionError } = useSubscription();
    const { isLoading: isLoadingSocialProfilesAuthStatus } = useSocialProfilesAuthStatus({
        revalidateIfStale: true
    });

    return (
        <ManageProfilesWrapper
            className={classNames({ 'h-full': isLoading || isLoadingSocialProfilesAuthStatus })}
            header={
                <AddSocialProfilesAction
                    isOpen={stateFunnelSocialProfiles.isOpen}
                    network={stateFunnelSocialProfiles.network}
                    idSocialProfileAdded={stateFunnelSocialProfiles.idSocialProfileAdded}
                    setState={setStateFunnelSocialProfiles}
                />
            }>
            {subscriptionError ? (
                <CompoundBanner className="mb-10">
                    <CompoundBanner.Icon />
                    <CompoundBanner.Content
                        title={tErrors('error_occured')}
                        message={tErrors('error_occured_fetch_data')}
                    />
                </CompoundBanner>
            ) : null}
            {isLoading || isLoadingSocialProfilesAuthStatus ? (
                <div className="flex h-full w-full items-center justify-center">
                    <Loader className="m-auto my-8 h-8 w-8 text-icoBlue" />
                </div>
            ) : (
                <div className="flex w-full flex-col gap-5 @[944px]/wrapper-platform-content:flex-row">
                    <SocialProfileContent
                        isOnTrial={isOnTrial}
                        toggleSPFunnelModal={() =>
                            setStateFunnelSocialProfiles((prevState) => ({
                                ...prevState,
                                isOpen: !prevState.isOpen
                            }))
                        }
                        plan={plan}
                    />
                </div>
            )}
        </ManageProfilesWrapper>
    );
};

const SocialProfiles = () => (
    <WrapperSwitchLayout>
        <PageContent />
    </WrapperSwitchLayout>
);

export const getServerSideProps =
    TranslationService.getTranslationMessagesFromRoute(SOCIAL_PROFILES);

export default SocialProfiles;
